/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   GrowthForecastPage.tsx
 *     Feature         :   217-insights-dashboards (T057 / US9, FR-011)
 *                         + Financial Growth & Forecast tab (Spec 259 follow-on)
 *     Description     :   Growth & Forecast Analytics — fleet-wide growth curves with
 *                         linear + weekday-seasonal projections. Pure read-side consumer of
 *                         dbo.insights_daily_metrics history — a metric with fewer than 2
 *                         days renders "not enough history," never a fabricated line.
 *
 *                         Two tabs: "Infrastructure & Usage" (all roles) and a "Financial"
 *                         tab (Admin/Manager only) that consolidates the REAL-billing cost
 *                         metrics so operators can spot rising-cost / negative financial
 *                         impact (egress + NAT gateway are the usual offenders) and so the
 *                         same daily-snapshotted metrics can drive alert rules.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { TrendingUp, TrendingDown, Minus, DollarSign, Boxes, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { apiClient } from '../../services/apiClient';
import { useCanViewCost } from '../../hooks/useCanViewCost';
import { PageLayout, Card, Badge, ErrorState, LoadingState, NotCollected, Extended, QuickGlance, Tabs } from '../../components/ui';
import type { TabItem } from '../../components/ui';

interface GrowthForecastSeries {
  metricKey: string;
  history: Array<{ date: string; value: number }>;
  slopePerDay: number;
  trend: 'up' | 'down' | 'flat';
  projected: Array<{ daysAhead: number; date: string; value: number }>;
}

interface GrowthForecastPayload {
  series: GrowthForecastSeries[];
  notEnoughHistory: string[];
}

const TREND_ICON: Record<GrowthForecastSeries['trend'], React.ReactNode> = {
  up: <TrendingUp className="h-3.5 w-3.5 text-[var(--color-status-warning)]" />,
  down: <TrendingDown className="h-3.5 w-3.5 text-[var(--color-status-success)]" />,
  flat: <Minus className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />,
};

type Unit = 'count' | 'gb' | 'usd';

// The Financial tab consolidates every metric in this group; everything else is Infra & Usage.
const FINANCIAL_GROUP = 'Financial';

// Human-readable label + unit for each rollup metric key. Keys without an explicit entry
// fall back to a de-slugified label (see metaFor) so a new metric never shows a raw key.
const METRIC_META: Record<string, { label: string; unit: Unit; group: string }> = {
  workspace_count: { label: 'Workspaces', unit: 'count', group: 'Fabric estate' },
  workspace_empty_count: { label: 'Empty workspaces', unit: 'count', group: 'Fabric estate' },
  'item_count.Lakehouse': { label: 'Lakehouses', unit: 'count', group: 'Fabric items' },
  'item_count.Warehouse': { label: 'Warehouses', unit: 'count', group: 'Fabric items' },
  'item_count.Notebook': { label: 'Notebooks', unit: 'count', group: 'Fabric items' },
  'item_count.Pipeline': { label: 'Data pipelines', unit: 'count', group: 'Fabric items' },
  'item_count.Report': { label: 'Reports', unit: 'count', group: 'Fabric items' },
  'item_count.SemanticModel': { label: 'Semantic models', unit: 'count', group: 'Fabric items' },
  warehouse_fleet_storage_gb: { label: 'Warehouse storage', unit: 'gb', group: 'Storage' },
  storage_fleet_gb: { label: 'Fleet storage', unit: 'gb', group: 'Storage' },
  storage_backup_gb: { label: 'Backup storage', unit: 'gb', group: 'Storage' },
  azure_resource_total_count: { label: 'Azure resources', unit: 'count', group: 'Azure estate' },
  azure_resource_public_count: { label: 'Public-network Azure resources', unit: 'count', group: 'Azure estate' },
  network_cidr_overlap_count: { label: 'CIDR overlaps', unit: 'count', group: 'Azure estate' },
  // Financial (REAL azure_cost_daily, trailing-30d run-rate — see the 3 retired modeled cost_* keys
  // in Spec 259). data_transfer + NAT gateway are the egress-cost signals from the WORM/NAT incident.
  cost_actual_total_daily_usd: { label: 'Total Azure spend', unit: 'usd', group: FINANCIAL_GROUP },
  cost_compute_usd: { label: 'Compute spend', unit: 'usd', group: FINANCIAL_GROUP },
  cost_storage_usd: { label: 'Storage spend', unit: 'usd', group: FINANCIAL_GROUP },
  cost_data_transfer_usd: { label: 'Data-transfer / egress spend', unit: 'usd', group: FINANCIAL_GROUP },
  cost_transactions_usd: { label: 'Transactions spend', unit: 'usd', group: FINANCIAL_GROUP },
  cost_nat_gateway_usd: { label: 'NAT gateway spend', unit: 'usd', group: FINANCIAL_GROUP },
};

function metaFor(key: string): { label: string; unit: Unit; group: string } {
  const hit = METRIC_META[key];
  if (hit) return hit;
  const base = key.replace(/^item_count\./, '').replace(/_(count|gb|usd)$/, '').replace(/_/g, ' ');
  const unit: Unit = key.endsWith('_usd') ? 'usd' : key.endsWith('_gb') ? 'gb' : 'count';
  return { label: base.charAt(0).toUpperCase() + base.slice(1), unit, group: 'Other' };
}

const isFinancialKey = (key: string): boolean => metaFor(key).group === FINANCIAL_GROUP;

function fmtValue(value: number, unit: Unit): string {
  if (unit === 'usd') return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  // GB -> TB is 1024, not 1000. The stored values are GiB (collectors divide bytes by 1024³),
  // so a decimal divisor made this page disagree with every other storage surface by ~2.4%.
  if (unit === 'gb') return value >= 1024
    ? `${(value / 1024).toLocaleString(undefined, { maximumFractionDigits: 2 })} TB`
    : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} GB`;
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// Projections need a reasonable run of days before a 90-day linear extrapolation is
// trustworthy — flag anything below this as directional-only.
const MIN_CONFIDENT_DAYS = 14;

function MetricCard({ series }: { series: GrowthForecastSeries }) {
  const meta = metaFor(series.metricKey);
  const combined = [
    ...series.history.map((p) => ({ date: p.date, actual: p.value, projected: null as number | null })),
    ...series.projected.map((p) => ({ date: p.date, actual: null as number | null, projected: p.value })),
  ];
  const at90 = series.projected.find((p) => p.daysAhead === 90);
  const current = series.history.length ? series.history[series.history.length - 1].value : null;
  const days = series.history.length;
  const lowConfidence = days < MIN_CONFIDENT_DAYS;
  // For storage/cost, growing (up) is the cost/capacity concern → warning; shrinking is fine.
  const trendVariant = series.trend === 'up' ? 'warning' : series.trend === 'down' ? 'success' : 'secondary';

  return (
    <Card>
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-[var(--color-text-primary)]">{meta.label}</h3>
          {current != null && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              Now <span className="font-medium text-[var(--color-text-primary)]">{fmtValue(current, meta.unit)}</span>
              <span className="text-[var(--color-text-secondary)]"> · {meta.group}</span>
            </p>
          )}
        </div>
        <Badge variant={trendVariant} isOutlined size="sm" leftIcon={TREND_ICON[series.trend]}>
          {series.trend}
        </Badge>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={combined}>
            <XAxis dataKey="date" tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 9 }} width={44} />
            <Tooltip formatter={(v) => (typeof v === 'number' ? fmtValue(v, meta.unit) : (v ?? '—'))} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line type="monotone" dataKey="actual" name="Actual" stroke="var(--color-accent)" strokeWidth={2} dot={false} connectNulls={false} />
            <Line type="monotone" dataKey="projected" name="Projected (est.)" stroke="var(--color-status-warning)" strokeWidth={2} strokeDasharray="4 4" dot={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {at90 && (
        <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
          Est. in 90 days: <span className="font-medium text-[var(--color-text-primary)]">{fmtValue(at90.value, meta.unit)}</span> (by {at90.date})
        </p>
      )}
      {lowConfidence && (
        <p className="mt-1 text-xs text-[var(--color-status-warning)]">
          ⚠ Based on only {days} day{days === 1 ? '' : 's'} of history — the projection is directional, not precise.
        </p>
      )}
    </Card>
  );
}

/** The shared "not enough history yet" note, scoped to whichever tab's metric set. */
function NotEnoughHistory({ keys }: { keys: string[] }) {
  if (keys.length === 0) return null;
  return (
    <Card>
      <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">Not enough history yet</h3>
      <p className="mb-2 text-xs text-[var(--color-text-secondary)]">{keys.length} metric(s) have fewer than 2 days of rollup data</p>
      <div className="flex flex-wrap gap-2">
        {keys.map((m) => <Badge key={m} variant="secondary" isOutlined size="sm">{metaFor(m).label}</Badge>)}
      </div>
    </Card>
  );
}

function MetricGrid({ series, emptyReason }: { series: GrowthForecastSeries[]; emptyReason: string }) {
  if (series.length === 0) return <Card><NotCollected reason={emptyReason} /></Card>;
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {series.map((s) => <MetricCard key={s.metricKey} series={s} />)}
    </div>
  );
}

/** Financial-tab header: the negative-impact readout — every cost metric that is trending up,
 *  worst first, with its 90-day projection. This is the "spot rising cost before it compounds"
 *  surface and the same data alert rules read from dbo.insights_daily_metrics. */
function FinancialImpactSummary({ series }: { series: GrowthForecastSeries[] }) {
  const rising = series
    .filter((s) => s.trend === 'up')
    .sort((a, b) => b.slopePerDay - a.slopePerDay);

  if (series.length === 0) return null;

  if (rising.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm text-[var(--color-status-success)]">
          <TrendingDown className="h-4 w-4" />
          <span>No financial metric is trending up — no rising-cost signal in the current window.</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-[var(--color-status-warning)] p-4">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-[var(--color-status-warning)]" />
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {rising.length} cost metric{rising.length === 1 ? '' : 's'} trending up
        </h3>
      </div>
      <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
        Spend is rising in these areas — review before it compounds. Data-transfer / egress and NAT gateway are the usual offenders.
      </p>
      <ul className="space-y-1.5">
        {rising.map((s) => {
          const meta = metaFor(s.metricKey);
          const now = s.history.length ? s.history[s.history.length - 1].value : null;
          const at90 = s.projected.find((p) => p.daysAhead === 90);
          return (
            <li key={s.metricKey} className="flex flex-wrap items-baseline gap-x-2 text-xs">
              <span className="font-medium text-[var(--color-text-primary)]">{meta.label}</span>
              {now != null && <span className="text-[var(--color-text-secondary)]">now {fmtValue(now, meta.unit)}</span>}
              {at90 && (
                <span className="text-[var(--color-status-warning)]">
                  → est. {fmtValue(at90.value, meta.unit)} in 90d
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function UsagePanel({ series, notEnough }: { series: GrowthForecastSeries[]; notEnough: string[] }) {
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <h3 className="mb-1 text-sm font-semibold text-[var(--color-text-primary)]">How to read this page</h3>
        <p className="text-xs text-[var(--color-text-secondary)]">
          Each card tracks one fleet metric over time. The <span className="font-medium text-[var(--color-accent)]">solid line</span> is the
          {' '}<span className="font-medium">actual</span> value collected each day; the{' '}
          <span className="font-medium text-[var(--color-status-warning)]">dashed line</span> is an{' '}
          <span className="font-medium">estimate</span> — a linear (least-squares) fit of the recent trend plus a weekday adjustment,
          extended 30/60/90 days out. The <span className="font-medium">up / flat / down</span> chip is the direction of that fit
          (for storage, up is the capacity concern, so it&apos;s amber; shrinking is green).
        </p>
        <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
          ⚠ Projections are only as good as the history behind them. A straight-line extrapolation over just a handful of days can
          overshoot badly and should be read as <span className="font-medium">directional only</span>. Accuracy firms up as history
          accumulates (cards with fewer than {MIN_CONFIDENT_DAYS} days are flagged).
        </p>
      </Card>
      <MetricGrid series={series} emptyReason="Fewer than 2 days of fleet-metric history collected yet — the rollup job needs at least one more day to tick" />
      <NotEnoughHistory keys={notEnough} />
    </div>
  );
}

function FinancialPanel({ series, notEnough }: { series: GrowthForecastSeries[]; notEnough: string[] }) {
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <div className="mb-1 flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-[var(--color-status-success)]" />
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Financial forecast — real Azure billing</h3>
        </div>
        <p className="text-xs text-[var(--color-text-secondary)]">
          Every figure here is <span className="font-medium">actual Azure Cost-Management billing</span> (from <code className="text-[var(--color-text-primary)]">dbo.azure_cost_daily</code>),
          expressed as a <span className="font-medium">trailing-30-day run-rate</span> snapshotted each day. The dashed line projects that
          run-rate forward. Up is the cost concern (amber); down is good (green). Compute + Storage + Data-transfer + Transactions sum to
          Total; NAT gateway is the portion of Data-transfer that drove the July egress spike.
        </p>
        <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
          These metrics are snapshotted to <code className="text-[var(--color-text-primary)]">dbo.insights_daily_metrics</code> daily, so alert
          rules can trigger on a rising financial trend. Admin / Manager only.
        </p>
      </Card>
      <FinancialImpactSummary series={series} />
      <MetricGrid series={series} emptyReason="No Azure billing history rolled up yet — once dbo.azure_cost_daily has 2+ days in the window, the financial forecast appears here" />
      <NotEnoughHistory keys={notEnough} />
    </div>
  );
}

export const GrowthForecastPage: React.FC = () => {
  const canSeeFinancial = useCanViewCost();

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // Guard: a non-privileged user deep-linking ?tab=financial falls back to usage.
  const tab: 'usage' | 'financial' = rawTab === 'financial' && canSeeFinancial ? 'financial' : 'usage';
  const onTabChange = (key: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  const TABS: TabItem[] = [
    { key: 'usage', label: 'Infrastructure & Usage', icon: <Boxes size={16} /> },
    ...(canSeeFinancial ? [{ key: 'financial', label: 'Financial', icon: <DollarSign size={16} /> }] : []),
  ];

  const query = useQuery<GrowthForecastPayload>({
    queryKey: ['growth-forecast-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: GrowthForecastPayload }>('/api/growth-forecast-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);

  useAutoRefresh(refresh, { lastFetchedAt });

  const financialSeries = (data?.series ?? []).filter((s) => isFinancialKey(s.metricKey));
  const usageSeries = (data?.series ?? []).filter((s) => !isFinancialKey(s.metricKey));
  const financialNotEnough = (data?.notEnoughHistory ?? []).filter(isFinancialKey);
  const usageNotEnough = (data?.notEnoughHistory ?? []).filter((k) => !isFinancialKey(k));

  return (
    <PageLayout
      title="Growth & Forecast Analytics"
      description="Fleet-wide growth curves with linear + seasonal projections — when do we outgrow current capacity, and where is spend heading?"
      icon={<TrendingUp className="h-6 w-6" />}
      helpId="insights.growth-forecast"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {error && !data ? (
        <ErrorState title="Failed to load growth forecast insights" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for per-metric growth curves.
            </Card>
          </QuickGlance>

          <Extended>
            <div className="space-y-6">
              {TABS.length > 1 && <Tabs tabs={TABS} value={tab} onChange={onTabChange} />}
              {tab === 'financial' && canSeeFinancial ? (
                <FinancialPanel series={financialSeries} notEnough={financialNotEnough} />
              ) : (
                <UsagePanel series={usageSeries} notEnough={usageNotEnough} />
              )}
            </div>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default GrowthForecastPage;
